import Pagination from '@material-ui/lab/Pagination'
import PaginationItem from '@material-ui/lab/PaginationItem'
import { Product, ReviewOfProduct, SpecificityOfProduct } from 'declarations'
import _ from 'lodash'
import moment from 'moment'
import qs from 'query-string'
import React from 'react'
import ReactLoading from 'react-loading'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { BookmarkOfProduct } from 'src/common/razzle-platform-bookmarkofproduct/BookmarkOfProduct'
import { useScroll, useWpPlugins } from 'src/common/razzle-platform-components/hooks'
import PageLoader from 'src/common/razzle-platform-components/PageLoader/PageLoader'
import { Link } from 'src/common/razzle-platform-components/router'
import { SEOEnhancer } from 'src/common/razzle-platform-components/seo/SEOEnhancer'
import { SSFC } from 'src/common/razzle-platform-components/types'
import { TeaserLocationMap } from 'src/common/razzle-platform-map/components/TeaserLocationMap/TeaserLocationMap'
import { useProduct } from 'src/common/razzle-platform-product/hooks'
import { getInitialPropsOfProduct } from 'src/common/razzle-platform-product/ssr'
import Reservation from 'src/common/razzle-platform-reservation/Reservation'
import { useReviewsOfPurchaseItem } from 'src/common/razzle-platform-reviewofpurchaseitem/hooks'
import { getInitialPropsOfReviewsOfPurchaseItem } from 'src/common/razzle-platform-reviewofpurchaseitem/ssr'
import { useSpecificitiesOfProduct } from 'src/common/razzle-platform-specificityofproduct/hooks'
import { getInitialPropsOfSpecificitiesOfProduct } from 'src/common/razzle-platform-specificityofproduct/ssr'
import { useIntl } from 'src/common/razzle-platform-translate'
import GoogleTranslate from 'src/common/razzle-platform-translate/Translate/GoogleTranslate'
import { useAuth } from 'src/common/razzle-platform-user/Authentication'
import {
    BASE_URL,
    CERTIFIED_VEHICLE_TAG_UUID,
    NUMBER_OF_BEDS_UUID,
    NUMBER_OF_PASSENGERS_UUID,
    RIGHT_RESERVATION_UUID,
    SPECIFICITIES_ORDERS,
    STATIC_PATH,
} from 'src/configs'
import { RatingSarts } from 'src/modules/components/RatingSarts'
import ConfirmButtonOfReservation from 'src/modules/product/pages/ProductDetails/components/reservation/ConfirmButton'
import DisplayDaysResultOfReservation from 'src/modules/product/pages/ProductDetails/components/reservation/DisplayDaysResult'
import IndicatorsOfReservation from 'src/modules/product/pages/ProductDetails/components/reservation/Indicators'
import { SwiperWithThumbs } from 'src/modules/product/pages/ProductDetails/components/SwiperWithThumbs/SwiperWithThumbs'
import { useMessaging } from 'src/modules/utils'
import { userRoutes } from 'src/routes'
import urlJoin from 'url-join'

const HEAD_SPECIFICITIES_UUIDS = [NUMBER_OF_BEDS_UUID, NUMBER_OF_PASSENGERS_UUID]
const PER_PAGE = 5

export const ProductDetails: SSFC<{}> = () => {
    const { formatMessage } = useIntl()
    useWpPlugins()
    useScroll()
    const { isAuthenticated, onAuth }: any = useAuth()
    // @ts-ignore
    const user = useSelector(({ userModel }) => userModel.user)

    const { product, isFirstRequestInPregress } = useProduct<Product>({ params: { _embed: ['user', 'tags'] } })
    const { specificitiesOfProduct } = useSpecificitiesOfProduct<SpecificityOfProduct>()
    const { handleSendMessageClick } = useMessaging(product?.user?.id)
    // get reviews of product
    const params = useParams()
    const id = _.get(params, 'id')
    const { reviews, pagination: reviewsPagination, isUpdateProgress } = useReviewsOfPurchaseItem<ReviewOfProduct>(id, {
        params: {
            _embed: 'user',
            _page: 1,
            _per_page: 3,
            _sort: '-created_at',
        },
    })

    const withBaseUrl = (path: string) => (path ? urlJoin(BASE_URL, path) : undefined)

    if (!product || isFirstRequestInPregress || !specificitiesOfProduct) {
        return <PageLoader />
    }

    // use this for reviews pagination
    const paginationItem = (item) => {
        const searchQuery = {
            ...qs.parse(location.search),
            _page: item.page,
        }
        const href = qs.stringifyUrl({ url: location.pathname, query: searchQuery })
        return <PaginationItem component={Link} to={href} {...item} />
    }

    const handleReservationClick = (event, openReservation) => {
        event.preventDefault()
        const authorization = {
            requiredRight: RIGHT_RESERVATION_UUID,
            description: formatMessage({
                id: 'Pour utiliser la réservation, vous devez être abonné',
                defaultMessage: 'Pour utiliser la réservation, vous devez être abonné',
            }),
            buttonTitle: formatMessage({ id: 'Abonnez-vous maintenant', defaultMessage: 'Abonnez-vous maintenant' }),
            redirectTo: `${BASE_URL}app/product-salable`,
        }
        onAuth((usr) => {
            if (usr.id !== product?.user?.id) {
                openReservation()
            }
        }, authorization)
    }

    let headSpecificities = []
    let otherSpecificities = []

    specificitiesOfProduct.forEach((specificity) => {
        const productSpec = product.productSpecificities.find((spec) => spec.specificity.id === specificity.id)
        if (productSpec && HEAD_SPECIFICITIES_UUIDS.indexOf(specificity.uuid) !== -1) {
            if (specificity.uuid === NUMBER_OF_BEDS_UUID) {
                headSpecificities.push({
                    ...productSpec,
                    shortName: formatMessage({ id: 'couchage', defaultMessage: 'couchage' }),
                })
            } else if (specificity.uuid === NUMBER_OF_PASSENGERS_UUID) {
                headSpecificities.push({
                    ...productSpec,
                    shortName: formatMessage({ id: 'passagers', defaultMessage: 'passagers' }),
                })
            } else {
                headSpecificities.push({ ...productSpec, shortName: productSpec.name })
            }
        }
    })

    product.productSpecificities.forEach((specificity) => {
        const isHeadSpec = headSpecificities.find((spec) => spec.specificity.id === specificity.specificity.id)
        if (!isHeadSpec) {
            otherSpecificities.push(specificity)
        }
    })

    const isMyProduct = !!(isAuthenticated() && user && user.id === product?.user?.id)

    const logoUrl = product.user.avatar?.default
        ? urlJoin(BASE_URL, product.user.avatar.default)
        : urlJoin(STATIC_PATH, 'assets/images/user_avatar.png')
    const shouldShowCertifiedVehicleTag = product?.tags?.some((tag) => tag.uuid === CERTIFIED_VEHICLE_TAG_UUID)
    return (
        <>
            <SEOEnhancer
                title={formatMessage(
                    {
                        id: '{name}  - Swaptheroad!!!',
                        defaultMessage: '{name}  - Swaptheroad!!!',
                    },
                    {
                        name: product.name,
                    },
                )}
                description={formatMessage(
                    {
                        id: 'Échangez ou réservez le véhicule de {name} et découvrez plein d’autres véhicules',
                        defaultMessage:
                            'Échangez ou réservez le véhicule de {name} et découvrez plein d’autres véhicules',
                    },
                    {
                        name: product.user.pseudo,
                    },
                )}
                imageUrl={!_.isEmpty(product.images) ? urlJoin(BASE_URL, product.mainImage.name['500x500']) : undefined}
            />

            <div
                data-elementor-type="wp-page"
                data-elementor-id="4977"
                className="elementor elementor-4977"
                data-elementor-post-type="page"
            >
                <section
                    className="elementor-section elementor-top-section elementor-element elementor-element-6027393c elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                    data-id="6027393c"
                    data-element_type="section"
                >
                    <div className="elementor-container elementor-column-gap-default">
                        <div
                            className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-4c8a052c"
                            data-id="4c8a052c"
                            data-element_type="column"
                        >
                            <div className="elementor-widget-wrap elementor-element-populated">
                                <div
                                    className="elementor-element elementor-element-5d4a8a7f elementor-widget elementor-widget-image"
                                    data-id="5d4a8a7f"
                                    data-element_type="widget"
                                    data-widget_type="image.default"
                                >
                                    <div className="elementor-widget-container">
                                        {_.isEmpty(product.images) ? (
                                            <img
                                                fetchpriority="high"
                                                decoding="async"
                                                width="800"
                                                height="600"
                                                src="https://www.swap-the-road.fr/wp-content/plugins/elementor/assets/images/placeholder.png"
                                                className="attachment-large size-large wp-image-12728"
                                                alt={product.name}
                                                sizes="(max-width: 800px) 100vw, 800px"
                                            />
                                        ) : (
                                            <SwiperWithThumbs
                                                alt={product.name}
                                                id={product.id}
                                                images={_.sortBy(product.images, [(image) => image.type]).map((image) =>
                                                    withBaseUrl(image.name['500x500']),
                                                )}
                                                hqImages={_.sortBy(product.images, [
                                                    (image) => image.type,
                                                ]).map((image) => withBaseUrl(image.name.original))}
                                            />
                                        )}
                                    </div>
                                </div>
                                <section
                                    className="elementor-section elementor-inner-section elementor-element elementor-element-289e90bf elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                                    data-id="289e90bf"
                                    data-element_type="section"
                                >
                                    <div className="elementor-container elementor-column-gap-default">
                                        {/* Title container */}
                                        <div
                                            className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-5ed38316"
                                            data-id="5ed38316"
                                            data-element_type="column"
                                        >
                                            <div className="elementor-widget-wrap elementor-element-populated">
                                                <div
                                                    className="elementor-element elementor-element-13376910 elementor-widget elementor-widget-heading"
                                                    data-id="13376910"
                                                    data-element_type="widget"
                                                    data-widget_type="heading.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <h1 className="elementor-heading-title elementor-size-default">
                                                            {product.name}
                                                        </h1>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Buttons container */}
                                        <div
                                            className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-61cb39b2"
                                            data-id="61cb39b2"
                                            data-element_type="column"
                                        >
                                            <div className="elementor-widget-wrap elementor-element-populated">
                                                {/* Bookmark button */}
                                                <div
                                                    className="elementor-element elementor-element-d0bf92d elementor-view-default elementor-widget elementor-widget-icon"
                                                    data-id="d0bf92d"
                                                    data-element_type="widget"
                                                    data-widget_type="icon.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <div className="elementor-icon-wrapper">
                                                            <BookmarkOfProduct
                                                                productId={product.id}
                                                                bookmarkButton={({
                                                                    onClick,
                                                                    isBookmarked,
                                                                    inProgress: isBookmarkingInProgress,
                                                                }) => (
                                                                    <a
                                                                        className="elementor-icon"
                                                                        href="#"
                                                                        onClick={onClick}
                                                                    >
                                                                        {isBookmarkingInProgress ? (
                                                                            <ReactLoading
                                                                                type={'spokes'}
                                                                                height={21}
                                                                                width={20}
                                                                            />
                                                                        ) : (
                                                                            <i
                                                                                aria-hidden="true"
                                                                                className={
                                                                                    isBookmarked
                                                                                        ? 'fas fa-heart'
                                                                                        : 'far fa-heart'
                                                                                }
                                                                            ></i>
                                                                        )}
                                                                    </a>
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Category button */}
                                                <div
                                                    className="elementor-element elementor-element-54d89a11 elementor-align-right elementor-mobile-align-center elementor-widget elementor-widget-button"
                                                    data-id="54d89a11"
                                                    data-element_type="widget"
                                                    data-widget_type="button.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <div className="elementor-button-wrapper">
                                                            <a
                                                                className="elementor-button elementor-button-link elementor-size-sm"
                                                                href="#"
                                                            >
                                                                <span className="elementor-button-content-wrapper">
                                                                    <span className="elementor-button-text">
                                                                        {product?.category?.name}
                                                                    </span>
                                                                </span>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section
                                    className="elementor-section elementor-inner-section elementor-element elementor-element-b82eef1 elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                                    data-id="b82eef1"
                                    data-element_type="section"
                                >
                                    <div className="elementor-container elementor-column-gap-default">
                                        {/* Left */}
                                        <div
                                            className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-eb5ec49"
                                            data-id="eb5ec49"
                                            data-element_type="column"
                                        >
                                            <div className="elementor-widget-wrap elementor-element-populated">
                                                <div
                                                    className="elementor-element elementor-element-3f79d719 elementor-widget elementor-widget-text-editor"
                                                    data-id="3f79d719"
                                                    data-element_type="widget"
                                                    data-widget_type="text-editor.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <p style={{ textAlign: 'left' }}>
                                                            {headSpecificities
                                                                .map(
                                                                    (spec) =>
                                                                        `${
                                                                            _.find(spec.choices, { value: spec.choice })
                                                                                ?.label
                                                                        } ${spec.shortName}`,
                                                                )
                                                                .join(' | ')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Middle */}
                                        <div
                                            className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-97d6f58"
                                            data-id="97d6f58"
                                            data-element_type="column"
                                        >
                                            <div className="elementor-widget-wrap elementor-element-populated">
                                                <div
                                                    className="elementor-element elementor-element-29702842 elementor-position-left elementor-view-default elementor-mobile-position-top elementor-widget elementor-widget-icon-box"
                                                    data-id="29702842"
                                                    data-element_type="widget"
                                                    data-widget_type="icon-box.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <div className="elementor-icon-box-wrapper">
                                                            <div className="elementor-icon-box-icon">
                                                                <span className="elementor-icon">
                                                                    <i
                                                                        aria-hidden="true"
                                                                        className="fas fa-map-marker-alt"
                                                                    ></i>
                                                                </span>
                                                            </div>

                                                            {product.address && (
                                                                <div className="elementor-icon-box-content">
                                                                    <h3 className="elementor-icon-box-title">
                                                                        <span>
                                                                            {product.address.country} |{' '}
                                                                            {product.address.city}
                                                                        </span>
                                                                    </h3>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Right */}
                                        {product.reviewScore > 0 && (
                                            <div
                                                className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-996e356"
                                                data-id="996e356"
                                                data-element_type="column"
                                            >
                                                <div className="elementor-widget-wrap elementor-element-populated">
                                                    <div
                                                        className="elementor-element elementor-element-6441ee4c elementor-star-rating--align-right elementor-star-rating-mobile--align-center elementor--star-style-star_fontawesome elementor-widget elementor-widget-star-rating"
                                                        data-id="6441ee4c"
                                                        data-element_type="widget"
                                                        data-widget_type="star-rating.default"
                                                    >
                                                        <div className="elementor-widget-container">
                                                            <div className="elementor-star-rating__wrapper">
                                                                <div className="elementor-star-rating__title">
                                                                    ({reviewsPagination?.count}{' '}
                                                                    {formatMessage({
                                                                        id: 'avis',
                                                                        defaultMessage: 'avis',
                                                                    })}
                                                                    )
                                                                </div>
                                                                <div
                                                                    className="elementor-star-rating"
                                                                    title={`${product.reviewScore}/5`}
                                                                    itemType="http://schema.org/Rating"
                                                                    itemScope={true}
                                                                    itemProp="reviewRating"
                                                                >
                                                                    <RatingSarts rating={product.reviewScore} />
                                                                    <span
                                                                        itemProp="ratingValue"
                                                                        className="elementor-screen-only"
                                                                    >
                                                                        {product.reviewScore}/5
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                                <div
                                    className="elementor-element elementor-element-fd9bdaf elementor-widget-divider--view-line elementor-widget elementor-widget-divider"
                                    data-id="fd9bdaf"
                                    data-element_type="widget"
                                    data-widget_type="divider.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-divider">
                                            <span className="elementor-divider-separator"></span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="elementor-element elementor-element-41a293ac elementor-nav-menu__align-start elementor-nav-menu--dropdown-tablet elementor-nav-menu__text-align-aside elementor-widget elementor-widget-nav-menu"
                                    data-id="41a293ac"
                                    data-element_type="widget"
                                    data-settings='{"layout":"horizontal","submenu_icon":{"value":"&lt;i className=\"fas fa-caret-down\" aria-hidden=\"true\"&gt;&lt;\/i&gt;","library":"fa-solid"}}'
                                    data-widget_type="nav-menu.default"
                                >
                                    <div className="elementor-widget-container">
                                        <nav
                                            aria-label="Menu"
                                            className="elementor-nav-menu--main elementor-nav-menu__container elementor-nav-menu--layout-horizontal e--pointer-none"
                                        >
                                            <ul id="menu-1-41a293ac" className="elementor-nav-menu">
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5130">
                                                    <a href="#vue" className="elementor-item elementor-item-anchor">
                                                        {formatMessage({
                                                            id: "Vue d'ensemble",
                                                            defaultMessage: "Vue d'ensemble",
                                                        })}
                                                    </a>
                                                </li>
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5132">
                                                    <a
                                                        href="#localisation"
                                                        className="elementor-item elementor-item-anchor"
                                                    >
                                                        {formatMessage({
                                                            id: 'Localisation',
                                                            defaultMessage: 'Localisation',
                                                        })}
                                                    </a>
                                                </li>
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5133">
                                                    <a href="#avis" className="elementor-item elementor-item-anchor">
                                                        {formatMessage({ id: 'Avis', defaultMessage: 'Avis' })}
                                                    </a>
                                                </li>
                                            </ul>
                                        </nav>
                                        <nav
                                            className="elementor-nav-menu--dropdown elementor-nav-menu__container"
                                            aria-hidden="true"
                                        >
                                            <ul id="menu-2-41a293ac" className="elementor-nav-menu">
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5130">
                                                    <a
                                                        href="#vue"
                                                        className="elementor-item elementor-item-anchor"
                                                        tabIndex={-1}
                                                    >
                                                        {formatMessage({
                                                            id: "Vue d'ensemble",
                                                            defaultMessage: "Vue d'ensemble",
                                                        })}
                                                    </a>
                                                </li>
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5132">
                                                    <a
                                                        href="#localisation"
                                                        className="elementor-item elementor-item-anchor"
                                                        tabIndex={-1}
                                                    >
                                                        {formatMessage({
                                                            id: 'Localisation',
                                                            defaultMessage: 'Localisation',
                                                        })}
                                                    </a>
                                                </li>
                                                <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-5133">
                                                    <a
                                                        href="#avis"
                                                        className="elementor-item elementor-item-anchor"
                                                        tabIndex={-1}
                                                    >
                                                        {formatMessage({ id: 'Avis', defaultMessage: 'Avis' })}
                                                    </a>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>

                                <div
                                    className="elementor-element elementor-element-695158e elementor-widget elementor-widget-menu-anchor"
                                    data-id="695158e"
                                    data-element_type="widget"
                                    data-widget_type="menu-anchor.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-menu-anchor" id="vue"></div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div
                                    className="elementor-element elementor-element-1bbbeb51 elementor-widget elementor-widget-text-editor"
                                    data-id="1bbbeb51"
                                    data-element_type="widget"
                                    data-widget_type="text-editor.default"
                                >
                                    <div className="elementor-widget-container">
                                        {product.description && (
                                            <GoogleTranslate text={product.description}>
                                                {({ text, TranslateButton, Loading }) => (
                                                    <>
                                                        {Loading ? (
                                                            <div style={{ marginBottom: 30 }}>
                                                                <Loading />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: text ? text.replace(/\r\n/g, '<br />') : '',
                                                                }}
                                                            />
                                                        )}
                                                        <div style={{ textAlign: 'center', marginBottom: 40 }}>
                                                            <TranslateButton />
                                                        </div>
                                                    </>
                                                )}
                                            </GoogleTranslate>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-6f5f4737"
                            data-id="6f5f4737"
                            data-element_type="column"
                            data-settings='{"background_background":"classic"}'
                        >
                            <div className="elementor-widget-wrap elementor-element-populated">
                                <div
                                    className="elementor-element elementor-element-1378a92 elementor-align-justify elementor-tablet-align-justify elementor-widget elementor-widget-button"
                                    data-id="1378a92"
                                    data-element_type="widget"
                                    data-widget_type="button.default"
                                >
                                    <div className="elementor-widget-container">
                                        {shouldShowCertifiedVehicleTag && (
                                            <div className="elementor-button-wrapper">
                                                <a
                                                    className="elementor-button elementor-button-link elementor-size-md"
                                                    href="#"
                                                >
                                                    <span className="elementor-button-content-wrapper">
                                                        <span className="elementor-button-icon">
                                                            <i aria-hidden="true" className="fas fa-check-circle"></i>
                                                        </span>
                                                        <span className="elementor-button-text">
                                                            {formatMessage({
                                                                id: 'Véhicule vérifié',
                                                                defaultMessage: 'Véhicule vérifié',
                                                            })}
                                                        </span>
                                                    </span>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-50b87843 elementor-widget elementor-widget-heading"
                                    data-id="50b87843"
                                    data-element_type="widget"
                                    data-widget_type="heading.default"
                                >
                                    <div className="elementor-widget-container">
                                        <h2 className="elementor-heading-title elementor-size-default">{`${
                                            product.price
                                        } ${formatMessage({ id: 'RP/jour', defaultMessage: 'RP/jour' })}`}</h2>
                                    </div>
                                </div>

                                <section
                                    className="elementor-section elementor-inner-section elementor-element elementor-element-3aab1780 elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                                    data-id="3aab1780"
                                    data-element_type="section"
                                    data-settings='{"sticky":"top","sticky_on":["desktop","tablet"],"sticky_offset":90,"sticky_effects_offset":0,"sticky_anchor_link_offset":0}'
                                >
                                    <div className="elementor-container elementor-column-gap-default">
                                        <div
                                            className="elementor-column elementor-col-100 elementor-inner-column elementor-element elementor-element-1ca99f27"
                                            data-id="1ca99f27"
                                            data-element_type="column"
                                            data-settings='{"background_background":"classic"}'
                                        >
                                            <div className="elementor-widget-wrap elementor-element-populated">
                                                <div
                                                    className="elementor-element elementor-element-3a77bfa8 elementor-position-top elementor-widget elementor-widget-image-box"
                                                    data-id="3a77bfa8"
                                                    data-element_type="widget"
                                                    data-widget_type="image-box.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <div className="elementor-image-box-wrapper">
                                                            <figure className="elementor-image-box-img">
                                                                <Link to={userRoutes.details(product.user)}>
                                                                    <img
                                                                        decoding="async"
                                                                        width="150"
                                                                        height="150"
                                                                        src={logoUrl}
                                                                        className="attachment-medium size-medium wp-image-13596"
                                                                        alt={product.user.pseudo || product.user.name}
                                                                        loading="lazy"
                                                                        sizes="100vw"
                                                                    />
                                                                </Link>
                                                            </figure>
                                                            <div className="elementor-image-box-content">
                                                                <Link to={userRoutes.details(product.user)}>
                                                                    <h4 className="elementor-image-box-title">
                                                                        {product.user.pseudo || product.user.name}
                                                                    </h4>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className="elementor-element elementor-element-3c04b69a elementor-widget elementor-widget-text-editor"
                                                    data-id="3c04b69a"
                                                    data-element_type="widget"
                                                    data-widget_type="text-editor.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <p>
                                                            {formatMessage({
                                                                id: 'Membre depuis',
                                                                defaultMessage: 'Membre depuis',
                                                            })}{' '}
                                                            : {moment(product.user.registeredAt).format('DD/MM/YYYY')}
                                                            <br />
                                                            {!_.isEmpty(product.user.languages) && (
                                                                <>
                                                                    {' '}
                                                                    {formatMessage({
                                                                        id: 'Langue(s) parlée(s)',
                                                                        defaultMessage: 'Langue(s) parlée(s)',
                                                                    })}
                                                                    :{' '}
                                                                    {product.user.languages
                                                                        .map((e) => e.name)
                                                                        .join(', ')}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                {!isMyProduct && (
                                                    <div
                                                        className="elementor-element elementor-element-f707b0 elementor-align-justify elementor-widget elementor-widget-button"
                                                        data-id="f707b0"
                                                        data-element_type="widget"
                                                        data-widget_type="button.default"
                                                    >
                                                        <div className="elementor-widget-container">
                                                            <div className="elementor-button-wrapper">
                                                                <a
                                                                    className="elementor-button elementor-button-link elementor-size-md"
                                                                    href="#"
                                                                    role="button"
                                                                    onClick={handleSendMessageClick}
                                                                >
                                                                    <span className="elementor-button-content-wrapper">
                                                                        <span className="elementor-button-text">
                                                                            {formatMessage({
                                                                                id: 'Envoyer un message',
                                                                                defaultMessage: 'Envoyer un message',
                                                                            })}
                                                                        </span>
                                                                    </span>
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {!isMyProduct && (
                                                    <div
                                                        className="elementor-element elementor-element-786a390 elementor-align-justify elementor-widget elementor-widget-button"
                                                        data-id="786a390"
                                                        data-element_type="widget"
                                                        data-widget_type="button.default"
                                                    >
                                                        <div className="elementor-widget-container">
                                                            <div className="elementor-button-wrapper">
                                                                <Reservation
                                                                    onClick={handleReservationClick}
                                                                    product={product}
                                                                    confirmButton={ConfirmButtonOfReservation}
                                                                    displayDaysResult={DisplayDaysResultOfReservation}
                                                                    indicators={IndicatorsOfReservation}
                                                                    reservationButton={({
                                                                        onClick,
                                                                        buttonRef,
                                                                        label,
                                                                    }) => (
                                                                        <a
                                                                            href="#"
                                                                            className="elementor-button elementor-button-link elementor-size-md"
                                                                            role="button"
                                                                            onClick={onClick}
                                                                            ref={buttonRef}
                                                                        >
                                                                            <span className="elementor-button-content-wrapper">
                                                                                <span className="elementor-button-text">
                                                                                    {label}
                                                                                </span>
                                                                            </span>
                                                                        </a>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </section>

                {otherSpecificities.length > 0 && (
                    <section
                        className="elementor-section elementor-top-section elementor-element elementor-element-469192c8 elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                        data-id="469192c8"
                        data-element_type="section"
                    >
                        <div className="elementor-container elementor-column-gap-default">
                            <div
                                className="elementor-column elementor-col-66 elementor-top-column elementor-element elementor-element-79696684"
                                data-id="79696684"
                                data-element_type="column"
                            >
                                <div className="elementor-widget-wrap elementor-element-populated">
                                    {SPECIFICITIES_ORDERS.map((value, index) => (
                                        <>
                                            <div
                                                className="elementor-element elementor-element-51f087a1 elementor-view-stacked elementor-position-left elementor-shape-circle elementor-mobile-position-top elementor-widget elementor-widget-icon-box"
                                                data-id="51f087a1"
                                                data-element_type="widget"
                                                data-widget_type="icon-box.default"
                                            >
                                                <div className="elementor-widget-container">
                                                    <div className="elementor-icon-box-wrapper">
                                                        <div className="elementor-icon-box-icon">
                                                            <span className="elementor-icon">
                                                                <i aria-hidden="true" className={value.icon}></i>
                                                            </span>
                                                        </div>

                                                        <div className="elementor-icon-box-content">
                                                            <h3 className="elementor-icon-box-title">
                                                                <span>
                                                                    {value.label === 'Caractéristiques téchniques' &&
                                                                        formatMessage({
                                                                            id: 'Caractéristiques téchniques',
                                                                            defaultMessage:
                                                                                'Caractéristiques téchniques',
                                                                        })}
                                                                    {value.label === 'Vie à bord' &&
                                                                        formatMessage({
                                                                            id: 'Vie à bord',
                                                                            defaultMessage: 'Vie à bord',
                                                                        })}
                                                                    {value.label === 'Autonomie' &&
                                                                        formatMessage({
                                                                            id: 'Autonomie',
                                                                            defaultMessage: 'Autonomie',
                                                                        })}
                                                                    {value.label === 'Equipement' &&
                                                                        formatMessage({
                                                                            id: 'Equipement',
                                                                            defaultMessage: 'Equipement',
                                                                        })}
                                                                </span>
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="elementor-element elementor-element-5359614d elementor-mobile-align-left elementor-icon-list--layout-inline elementor-list-item-link-full_width elementor-widget elementor-widget-icon-list"
                                                data-id="5359614d"
                                                data-element_type="widget"
                                                data-widget_type="icon-list.default"
                                            >
                                                <div className="elementor-widget-container">
                                                    <ul className="elementor-icon-list-items elementor-inline-items">
                                                        {value.orders.map((uuid) => {
                                                            const specificity = _.find(
                                                                otherSpecificities,
                                                                (item) => item.uuid === uuid,
                                                            )

                                                            return specificity ? (
                                                                <li
                                                                    className="elementor-icon-list-item elementor-inline-item"
                                                                    key={uuid}
                                                                >
                                                                    <span className="elementor-icon-list-icon">
                                                                        <i
                                                                            aria-hidden="true"
                                                                            className={`fas fa-${
                                                                                specificity.type === 'boolean' &&
                                                                                !specificity.choice
                                                                                    ? 'times text-red-600'
                                                                                    : 'check'
                                                                            }`}
                                                                        ></i>
                                                                    </span>
                                                                    <span className="elementor-icon-list-text">
                                                                        {specificity.type === 'm_list'
                                                                            ? `${specificity.name}: ${specificity.choice
                                                                                  .map(
                                                                                      (item) =>
                                                                                          _.find(specificity.choices, {
                                                                                              value: item,
                                                                                          })?.label,
                                                                                  )
                                                                                  .join(', ')}`
                                                                            : specificity.type === 'list'
                                                                            ? `${specificity.name}: ${
                                                                                  _.find(specificity.choices, {
                                                                                      value: specificity.choice,
                                                                                  })?.label
                                                                              }`
                                                                            : specificity.type === 'boolean'
                                                                            ? specificity.name
                                                                            : `${specificity.name}: ${specificity.choice}`}
                                                                    </span>
                                                                </li>
                                                            ) : null
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </>
                                    ))}
                                </div>
                            </div>
                            <div
                                className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-b502a23"
                                data-id="b502a23"
                                data-element_type="column"
                            >
                                <div className="elementor-widget-wrap"></div>
                            </div>
                        </div>
                    </section>
                )}
                <section
                    className="elementor-section elementor-top-section elementor-element elementor-element-6bdd0ac9 elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                    data-id="6bdd0ac9"
                    data-element_type="section"
                >
                    <div className="elementor-container elementor-column-gap-default">
                        <div
                            className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-6812bb0b"
                            data-id="6812bb0b"
                            data-element_type="column"
                        >
                            <div className="elementor-widget-wrap elementor-element-populated">
                                <div
                                    className="elementor-element elementor-element-5dd9ca2 elementor-widget-divider--view-line elementor-widget elementor-widget-divider"
                                    data-id="5dd9ca2"
                                    data-element_type="widget"
                                    data-widget_type="divider.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-divider">
                                            <span className="elementor-divider-separator"> </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-38c3bb45 elementor-widget elementor-widget-menu-anchor"
                                    data-id="38c3bb45"
                                    data-element_type="widget"
                                    data-widget_type="menu-anchor.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-menu-anchor" id="localisation"></div>
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-38474aff elementor-widget elementor-widget-heading"
                                    data-id="38474aff"
                                    data-element_type="widget"
                                    data-widget_type="heading.default"
                                >
                                    <div className="elementor-widget-container">
                                        <h2 className="elementor-heading-title elementor-size-default">
                                            {formatMessage({ id: 'Location', defaultMessage: 'Location' })}
                                        </h2>
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-b6bf36a elementor-cta--skin-cover elementor-cta--valign-middle elementor-bg-transform elementor-bg-transform-zoom-out raz-teaser-map elementor-animated-content elementor-widget elementor-widget-call-to-action"
                                    data-id="b6bf36a"
                                    data-element_type="widget"
                                    style={{ height: 299 }}
                                    data-widget_type="call-to-action.default"
                                >
                                    <TeaserLocationMap
                                        latitude={product.address.latitude}
                                        longitude={product.address.longitude}
                                        zoom={15}
                                    >
                                        {({ onClick }) => (
                                            <div className="elementor-widget-container">
                                                <a className="elementor-cta" onClick={onClick} href="#">
                                                    <div className="elementor-cta__bg-wrapper">
                                                        <div
                                                            className="elementor-cta__bg elementor-bg"
                                                            style={{
                                                                backgroundImage:
                                                                    'url(https://www.swaptheroad.com/wp-content/uploads/2023/11/map-preview-1024x515.png)',
                                                            }}
                                                            role="img"
                                                            aria-label="map-preview.png"
                                                        ></div>
                                                        <div className="elementor-cta__bg-overlay"></div>
                                                    </div>
                                                    <div className="elementor-cta__content">
                                                        <div className="elementor-cta__button-wrapper elementor-cta__content-item elementor-content-item elementor-animated-item--grow">
                                                            <span className="elementor-cta__button elementor-button elementor-size-">
                                                                {formatMessage({
                                                                    id: 'Display map',
                                                                    defaultMessage: 'Display map',
                                                                })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </a>
                                            </div>
                                        )}
                                    </TeaserLocationMap>
                                </div>
                                <div
                                    className="elementor-element elementor-element-1ceb78d elementor-widget-divider--view-line elementor-widget elementor-widget-divider"
                                    data-id="1ceb78d"
                                    data-element_type="widget"
                                    data-widget_type="divider.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-divider">
                                            <span className="elementor-divider-separator"> </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-632761ad elementor-widget elementor-widget-menu-anchor"
                                    data-id="632761ad"
                                    data-element_type="widget"
                                    data-widget_type="menu-anchor.default"
                                >
                                    <div className="elementor-widget-container">
                                        <div className="elementor-menu-anchor" id="avis"></div>
                                    </div>
                                </div>
                                <div
                                    className="elementor-element elementor-element-6da01620 elementor-widget elementor-widget-heading"
                                    data-id="6da01620"
                                    data-element_type="widget"
                                    data-widget_type="heading.default"
                                >
                                    <div className="elementor-widget-container">
                                        <h2 className="elementor-heading-title elementor-size-default">
                                            {formatMessage({ id: 'Avis', defaultMessage: 'Avis' })}
                                        </h2>
                                    </div>
                                </div>

                                {reviews?.map((review, index) => {
                                    const logoUrl = review.user.avatar?.default
                                        ? urlJoin(BASE_URL, review.user.avatar.default)
                                        : urlJoin(STATIC_PATH, 'assets/images/user_avatar.png')

                                    return (
                                        <React.Fragment key={review.id}>
                                            {index > 0 && (
                                                <div
                                                    className="elementor-element elementor-element-5d88a24 elementor-widget-divider--view-line elementor-widget elementor-widget-divider"
                                                    data-id="5d88a24"
                                                    data-element_type="widget"
                                                    data-widget_type="divider.default"
                                                >
                                                    <div className="elementor-widget-container">
                                                        <div className="elementor-divider">
                                                            <span className="elementor-divider-separator"> </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <section
                                                className="elementor-section elementor-inner-section elementor-element elementor-element-74a1b57 elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                                                data-id="74a1b57"
                                                data-element_type="section"
                                            >
                                                <div className="elementor-container elementor-column-gap-default">
                                                    <div
                                                        className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-57582299"
                                                        data-id="57582299"
                                                        data-element_type="column"
                                                    >
                                                        <div className="elementor-widget-wrap elementor-element-populated">
                                                            <div
                                                                className="elementor-element elementor-element-69c51417 elementor-widget elementor-widget-image"
                                                                data-id="69c51417"
                                                                data-element_type="widget"
                                                                data-widget_type="image.default"
                                                            >
                                                                <div className="elementor-widget-container">
                                                                    <img
                                                                        decoding="async"
                                                                        width="150"
                                                                        height="150"
                                                                        src={logoUrl}
                                                                        className="attachment-thumbnail size-thumbnail wp-image-5390"
                                                                        alt={`${
                                                                            review.user.pseudo || review.user.name
                                                                        } avatar`}
                                                                        sizes="(max-width: 150px) 100vw, 150px"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-767ca043"
                                                        data-id="767ca043"
                                                        data-element_type="column"
                                                    >
                                                        <div className="elementor-widget-wrap elementor-element-populated">
                                                            <div
                                                                className="elementor-element elementor-element-7887b1a3 elementor-widget elementor-widget-text-editor"
                                                                data-id="7887b1a3"
                                                                data-element_type="widget"
                                                                data-widget_type="text-editor.default"
                                                            >
                                                                <div className="elementor-widget-container">
                                                                    <p>
                                                                        {review.user.pseudo || review.user.name}
                                                                        <br />
                                                                        {moment(review.createdAt).format('DD/MM/YYYY')}
                                                                    </p>
                                                                    <p>{review.comment}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="elementor-column elementor-col-33 elementor-inner-column elementor-element elementor-element-f2870ce"
                                                        data-id="f2870ce"
                                                        data-element_type="column"
                                                    >
                                                        <div className="elementor-widget-wrap elementor-element-populated">
                                                            <div
                                                                className="elementor-element elementor-element-1b57170f elementor-star-rating--align-right elementor--star-style-star_fontawesome elementor-widget elementor-widget-star-rating"
                                                                data-id="1b57170f"
                                                                data-element_type="widget"
                                                                data-widget_type="star-rating.default"
                                                            >
                                                                <div className="elementor-widget-container">
                                                                    <div className="elementor-star-rating__wrapper">
                                                                        <div
                                                                            className="elementor-star-rating"
                                                                            title={`${review.note}/5`}
                                                                            itemType="http://schema.org/Rating"
                                                                            itemScope={true}
                                                                            itemProp="reviewRating"
                                                                        >
                                                                            <RatingSarts rating={review.note} />
                                                                            <span
                                                                                itemProp="ratingValue"
                                                                                className="elementor-screen-only"
                                                                            >{`${review.note}/5`}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        </React.Fragment>
                                    )
                                })}
                                <section
                                    className="elementor-section elementor-inner-section elementor-element elementor-element-7a0b8b6d elementor-section-boxed elementor-section-height-default elementor-section-height-default"
                                    data-id="7a0b8b6d"
                                    data-element_type="section"
                                >
                                    <div className="elementor-container elementor-column-gap-default"></div>
                                </section>
                                {reviewsPagination?.count >= PER_PAGE && (
                                    <div
                                        className="elementor-element elementor-element-5a0a695c pagnation elementor-widget elementor-widget-html"
                                        data-id="5a0a695c"
                                        data-element_type="widget"
                                        data-widget_type="html.default"
                                    >
                                        <div className="elementor-widget-container">
                                            <Pagination
                                                color="primary"
                                                count={Math.ceil(reviewsPagination?.count / reviewsPagination?.perPage)}
                                                variant="outlined"
                                                shape="rounded"
                                                page={reviewsPagination?.page}
                                                onChange={(e, page) => {
                                                    // setFilter({_page: page}, true)
                                                    const element = document.querySelector('#paperScrollTarget')
                                                    element?.scrollIntoView({ block: 'end', behavior: 'smooth' })
                                                    e.preventDefault()
                                                }}
                                                renderItem={paginationItem}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}

ProductDetails.getServerSideProps = async (context, store) => {
    await getInitialPropsOfProduct(context, store, { params: { _embed: ['user', 'tags'] } })
    await getInitialPropsOfSpecificitiesOfProduct(context, store)
    // get the reviews of product
    const { id } = context.route.params
    const params = {
        _embed: 'user',
        _page: 1,
        _per_page: 3,
        _sort: '-created_at',
    }

    await getInitialPropsOfReviewsOfPurchaseItem(context, store, id, { params })
}
